import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import type { LayoutChangeEvent, ViewProps } from 'react-native';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import type { SharedValue } from 'react-native-reanimated';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useIsFocused } from '@react-navigation/native';
import { theme } from '@sg/garnish';

import {
  useLocationResultsExpandCollapse,
  useLocationResultsScrolling,
} from './hooks';
import {
  LOCATION_RESULTS_BORDER_RADIUS,
  LOCATION_RESULTS_CARD_HEIGHT,
  LOCATION_RESULTS_OTHER_LOCATIONS_TITLE_HEIGHT,
  LOCATION_RESULTS_TOGGLE_BTN_HEIGHT,
} from './LocationResultsV2.constants';
import {
  LocationResultsBackdrop,
  LocationResultsBottomShadow,
  LocationResultsLists,
  LocationResultsMoreLocationsToggle,
} from './subcomponents';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders location lists in a dynamic container that can be expanded and collapsed.
 * It employs a two-container (outer and inner) approach to correctly calculate
 * collapsed and expanded layouts, as well as smooth animations utilizing the
 * `translateY` animated style to avoid repainting.
 *
 * It supports the following modes/states:
 *
 * - Loading state
 * - A single location view
 * - Multiple locations
 * - Multiple locations + other locations
 */
export const LocationResultsV2 = (props: LocationResultsV2Props) => {
  const {
    locations = [],
    otherLocations = [],
    locationSearchType,
    activePinLocationId,
    activeReorderRestaurantSlug,
    isLoading,
    shouldHideDetailsModal,
    onCardPress,
  } = props;

  const isFocused = useIsFocused();

  const totalLocationsNumber = locations.length + otherLocations.length;
  const hasNoResults = !isLoading && totalLocationsNumber === 0;
  const hasSingleResult = totalLocationsNumber === 1;
  const hasSingleOtherResult = hasSingleResult && otherLocations.length === 1;
  const hasMultipleResults = totalLocationsNumber > 1;
  const shouldUseDetailsModal =
    locationSearchType !== 'delivery' && !shouldHideDetailsModal;
  const shouldNotRender = hasNoResults || !isFocused;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const { isExpanded, toggleExpandedState } = useLocationResultsExpandCollapse({
    locationSearchType,
    hasSingleResult,
  });
  const {
    listScrollViewRef,
    setListItemScrollPosition,
    deleteListItemScrollPosition,
  } = useLocationResultsScrolling({
    activePinLocationId,
    isExpanded,
    locationSearchType,
  });

  // ─── Styles ──────────────────────────────────────────────────────────

  // NOTE: We utilize viewport height as a default value to completely hide the results
  //       container on initial render until it is managed by the associated component.
  const { height: viewportHeight } = useWindowDimensions();
  const outerContainerHeight = useSharedValue(viewportHeight);

  // ─────────────────────────────────────────────────────────────────────

  if (shouldNotRender) return null;

  return (
    <>
      <LocationResultsBackdrop isVisible={isExpanded} />
      <LocationResultsBottomShadow hasMultipleResults={hasMultipleResults} />

      <LocationResultsOuterContainer
        outerContainerHeight={outerContainerHeight}
      >
        <LocationResultsInnerContainer
          outerContainerHeight={outerContainerHeight}
          hasSingleOtherResult={hasSingleOtherResult}
          hasMultipleResults={hasMultipleResults}
          isExpanded={isExpanded}
        >
          {hasMultipleResults ? (
            <LocationResultsMoreLocationsToggle
              isToggled={isExpanded}
              onPress={toggleExpandedState}
            />
          ) : null}

          <LocationResultsLists
            ref={listScrollViewRef}
            locationSearchType={locationSearchType}
            locations={locations}
            otherLocations={otherLocations}
            activeReorderRestaurantSlug={activeReorderRestaurantSlug}
            isLoading={isLoading}
            withDetailsModal={shouldUseDetailsModal}
            containerHeightSharedValue={outerContainerHeight}
            hasSingleResult={hasSingleResult}
            isExpanded={isExpanded}
            onCardLayout={setListItemScrollPosition}
            onCardPress={onCardPress}
            onCardUnmount={deleteListItemScrollPosition}
          />
        </LocationResultsInnerContainer>
      </LocationResultsOuterContainer>
    </>
  );
};

// ─── Subcomponents ───────────────────────────────────────────────────────────

/**
 * Renders a `View` container component that fills available space and synchronizes
 * its height with the associated shared value for subsequent calculations.
 */
const LocationResultsOuterContainer = (
  props: LocationResultsOuterContainerProps,
) => {
  const { children, outerContainerHeight } = props;

  const setContainerHeightValue = useCallback(
    (event: LayoutChangeEvent) => {
      const { layout } = event.nativeEvent;

      // eslint-disable-next-line functional/immutable-data
      outerContainerHeight.value = withTiming(layout.height);
    },
    [outerContainerHeight],
  );

  return (
    <View
      style={styles.outerContainer}
      onLayout={setContainerHeightValue}
      pointerEvents="box-none"
    >
      {children}
    </View>
  );
};

/**
 * Renders an animated `View` component that can be expanded/collapsed externally using shared values
 * to use smooth animations and prevent repainting.
 */
const LocationResultsInnerContainer = (
  props: LocationResultsInnerContainerProps,
) => {
  const {
    children,
    isExpanded,
    outerContainerHeight,
    hasMultipleResults,
    hasSingleOtherResult,
  } = props;

  const innerContainerTranslateYOffset = hasMultipleResults
    ? LOCATION_RESULTS_TOGGLE_BTN_HEIGHT
    : hasSingleOtherResult
    ? LOCATION_RESULTS_OTHER_LOCATIONS_TITLE_HEIGHT
    : 0;

  /**
   * Returns a shared value based on the outer container height, single location result card height,
   * and optional offset to expand/collapse the results inner container.
   */
  const innerContainerTranslateY = useDerivedValue(() => {
    const expandedViewTranslateY = 0;

    // NOTE: When the `Animated.View` is collapsed, it should display a single location
    //       result card with a conditional "more locations" toggle.
    const collapsedViewTranslateY =
      outerContainerHeight.value -
      LOCATION_RESULTS_CARD_HEIGHT -
      innerContainerTranslateYOffset;

    const targetValue = isExpanded
      ? expandedViewTranslateY
      : collapsedViewTranslateY;

    return withTiming(targetValue);
  });

  const innerContainerAnimatedStyle = useAnimatedStyle(() => {
    return { transform: [{ translateY: innerContainerTranslateY.value }] };
  });
  const innerContainerStyles = [
    styles.innerContainer,
    innerContainerAnimatedStyle,
  ];

  return (
    <Animated.View style={innerContainerStyles} pointerEvents="box-none">
      {children}
    </Animated.View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: theme.zIndex.fixed,
    borderRadius: LOCATION_RESULTS_BORDER_RADIUS,
    margin: theme.spacing.medium,
    overflow: 'hidden',
  },
  innerContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsV2Props = {
  readonly activePinLocationId: string | undefined;
  readonly shouldHideDetailsModal: boolean;
} & Pick<
  ComponentProps<typeof LocationResultsLists>,
  | 'locations'
  | 'otherLocations'
  | 'locationSearchType'
  | 'activeReorderRestaurantSlug'
  | 'isLoading'
  | 'onCardPress'
>;

type LocationResultsOuterContainerProps = {
  readonly children: ViewProps['children'];
  readonly outerContainerHeight: SharedValue<number>;
};

type LocationResultsInnerContainerProps = {
  readonly children: ViewProps['children'];
  readonly outerContainerHeight: SharedValue<number>;
  readonly hasMultipleResults: boolean;
  readonly hasSingleOtherResult: boolean;
  readonly isExpanded: boolean;
};
